import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

/**
 * Dialog that lets the user paste a JSON array of DMS records
 * (`{ modelId, recordId }` pairs) to authorize for knowledge-base retrieval
 * during the next message sent to the agent.
 *
 * This is purely an ephemeral, per-message UI input - nothing here is
 * persisted.
 */
@ngCore.Component({
  imports: [
    ds.overlay.centered.Component,
    ds.layout.widget.Component,
    ds.input.code.Component,
    ds.button.Component,
  ],
  selector: 'atn-assistant-knowledge-base-retrieval-dialog',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Records to prefill the editor with when the dialog is opened */
  public readonly recordsSg = ngCore.input<
    readonly ents.core.agent.KnowledgeBase.Retrieval.Record[] | undefined
  >(undefined, { alias: 'records' })

  /**
   * Whether the records were supplied externally and can't be edited here
   * (e.g. provided as session context by a mosaic action).
   */
  public readonly readonlySg = ngCore.input<boolean>(false, {
    alias: 'readonly',
  })

  /** Current (in-progress) JSON value shown in the code editor */
  public readonly jsonSg = ngCore.signal<unknown>(undefined)

  /** Validation error message, set when the pasted JSON has the wrong shape */
  public readonly errorSg = ngCore.signal<string | undefined>(undefined)

  /** Emits the parsed records on save (`undefined` clears the selection) */
  public readonly save = ngCore.output<
    readonly ents.core.agent.KnowledgeBase.Retrieval.Record[] | undefined
  >()

  /** Emits when the dialog is dismissed without saving */
  public readonly cancel = ngCore.output<void>()

  constructor() {
    ngCore.effect(() => {
      this.jsonSg.set(this.recordsSg())
    })
  }

  /** Validates the pasted JSON and emits {@link save} if it looks correct */
  public onSaveClick(): void {
    if (this.readonlySg()) {
      return
    }

    const value = this.jsonSg()

    if (value === undefined) {
      this.errorSg.set(undefined)
      this.save.emit(undefined)
      return
    }

    if (!_isRecordsArray(value)) {
      this.errorSg.set(
        'Expected a JSON array of { "modelId": string, "recordId": string } objects.',
      )
      return
    }

    this.errorSg.set(undefined)
    this.save.emit(value)
  }

  /** Dismisses the dialog without saving */
  public onCancelClick(): void {
    this.cancel.emit()
  }
}

/** Narrows `value` to an array of {@link ents.core.agent.KnowledgeBase.Retrieval.Record} */
function _isRecordsArray(
  value: unknown,
): value is readonly ents.core.agent.KnowledgeBase.Retrieval.Record[] {
  return (
    Array.isArray(value) &&
    value.every(
      item =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Record<string, unknown>).modelId === 'string' &&
        typeof (item as Record<string, unknown>).recordId === 'string',
    )
  )
}
